import { Colors, Heading, Label, SelectFilled, Text } from '@amelio/ui-library';

import { COMPENSATION_SPLIT_ROW } from '../compensationStyles';

import type { CompensationCycle } from '@/types/compensation';
import type { OptionProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

interface CompensationHeaderProps {
    cycles: CompensationCycle[];
    selectedCycleId: string | undefined;
    onCycleChange: (cycleId: string) => void;
}

const CYCLE_SELECT_ID = 'compensation-cycle-select';

/** Title, subtitle and the evaluation-cycle selector every other section of the report hangs off:
 * an envelope always belongs to one cycle, so changing it changes the whole page. */
export const CompensationHeader = ({
    cycles,
    selectedCycleId,
    onCycleChange,
}: CompensationHeaderProps) => {
    const { t } = useAmelioTranslations();

    // Kept in the backend's own order (most recent cycle first), hence `sort='none'`: an
    // alphabetical list of cycle names would bury the one the reader almost always wants.
    const cycleOptions: OptionProps[] = cycles.map((cycle) => ({
        value: cycle.id,
        label: cycle.title,
    }));

    return (
        <div
            className={COMPENSATION_SPLIT_ROW}
            data-testid={'compensation-header'}
        >
            <div className={'flex flex-col gap-1'}>
                <Heading
                    level={1}
                    color={Colors.NAVY}
                    className={'text-[24px] font-medium'}
                >
                    {t('compensation.header.title')}
                </Heading>

                <Text color={Colors.BASE_500}>{t('compensation.header.subtitle')}</Text>
            </div>

            <div className={'flex flex-col gap-1'}>
                <Label
                    htmlFor={CYCLE_SELECT_ID}
                    color={Colors.BASE_500}
                >
                    {t('compensation.header.cycleLabel')}
                </Label>

                <div className={'max-sm:w-full sm:w-[240px]'}>
                    <SelectFilled
                        id={CYCLE_SELECT_ID}
                        options={cycleOptions}
                        value={selectedCycleId}
                        onChange={onCycleChange}
                        placeholder={t('compensation.header.cyclePlaceholder')}
                        sort={'none'}
                        dataTestId={'compensation-cycle-select'}
                    />
                </div>
            </div>
        </div>
    );
};
